import { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const API_BASE_URL = API_URL;

/**
 * Custom hook for handling VTO-aware deletion operations
 * Provides functions to check VTO counts and perform cascading deletions
 */
const useVTODeletion = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check how many VTO results are associated with an item
   * @param {number} itemId - ID of the item to check
   * @param {string} itemType - Type of item ('person-image' or 'clothing-item')
   * @returns {Promise<number>} - Count of associated VTO results
   */
  const checkVTOCount = async (itemId, itemType) => {
    setIsChecking(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const endpoint = itemType === 'person-image'
        ? `/person-images/${itemId}/vto-count`
        : `/closet-clothes/${itemId}/vto-count`;

      console.log(`[VTODel] Checking VTO count for ${itemType} ${itemId} at ${API_BASE_URL}${endpoint}`);

      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log(`[VTODel] VTO count check response:`, response.data);
      console.log(`[VTODel] VTO count for ${itemType} ${itemId}: ${response.data.count}`);
      
      return response.data.count;

    } catch (err) {
      console.error('[VTODel] Error checking VTO count:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to check VTO results';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsChecking(false);
    }
  };

  /**
   * Delete an item and all associated VTO results
   * @param {number} itemId - ID of the item to delete
   * @param {string} itemType - Type of item ('person-image' or 'clothing-item')
   * @returns {Promise<object>} - Deletion result with count of VTO results deleted
   */
  const deleteWithCascade = async (itemId, itemType) => {
    setIsDeleting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const endpoint = itemType === 'person-image'
        ? `/person-images/${itemId}/cascade`
        : `/closet-clothes/${itemId}/cascade`;

      const response = await axios.delete(`${API_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log(`[VTODel] Cascade delete completed for ${itemType} ${itemId}:`, response.data);
      return response.data;

    } catch (err) {
      console.error('Error in cascade delete:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete item';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    checkVTOCount,
    deleteWithCascade,
    isChecking,
    isDeleting,
    error
  };
};

export default useVTODeletion;
